import { useState, useEffect } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from '@/hooks/use-auth';
import type { TrainingPlan } from '@/types/curriculum';

interface ActivePlanData {
  id: string;
  plan_json: TrainingPlan;
  created_at: string;
  skill_level: string;
  swing_quality: number;
}

export function useActivePlan() {
  const { user } = useAuth();
  const [activePlan, setActivePlan] = useState<ActivePlanData | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const checkActivePlan = async () => {
    if (!user) {
      setActivePlan(null);
      setLoading(false);
      return;
    }

    try {
      setLoading(true);
      setError(null);

      const { data, error: fetchError } = await supabase
        .from('user_training_plans')
        .select('id, plan_json, created_at, skill_level, swing_quality')
        .eq('user_id', user.id)
        .eq('status', 'active')
        .order('created_at', { ascending: false })
        .limit(1)
        .maybeSingle();

      if (fetchError) {
        console.error('Error checking active plan:', fetchError);
        setError('Failed to check active plan');
        return;
      }

      setActivePlan(data);
    } catch (err) {
      console.error('Unexpected error checking active plan:', err);
      setError('An unexpected error occurred');
    } finally {
      setLoading(false);
    }
  };

  const archiveActivePlan = async (): Promise<boolean> => {
    if (!activePlan) return false;

    try {
      const { error } = await supabase
        .from('user_training_plans')
        .update({ 
          status: 'archived',
          updated_at: new Date().toISOString()
        })
        .eq('id', activePlan.id);

      if (error) {
        console.error('Error archiving plan:', error);
        return false;
      }

      setActivePlan(null);
      return true;
    } catch (err) {
      console.error('Unexpected error archiving plan:', err);
      return false;
    }
  };

  useEffect(() => {
    checkActivePlan();
  }, [user]);

  return {
    activePlan,
    loading,
    error,
    hasActivePlan: !!activePlan,
    checkActivePlan,
    archiveActivePlan,
  };
}
